import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CommentReport,
  CommentReportList,
  ModerationDecision,
  ModerationDecisionList,
} from '@up2d8/contracts';
import { problemDetail } from '@up2d8/api-client';
import { useToast } from '@up2d8/ui';
import {
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardHead,
  Empty,
  PageHead,
  Tile,
  useAdminToast,
} from '../admin-kit';
import { CardLoading, LoadError } from '../opvolging/parts';
import { relativeMoment } from '../opvolging/format';
import { formatDateTime } from '../../shared/format';
import { useSession } from '../../shared/session';

/**
 * De moderatiewachtrij (Stap 26), in het ontwerp van BHR-7 (V5): een smalle pagina met één kaart.
 *
 * Meest gemelde reactie bovenaan, met de tekst erbij. Dat laatste is onvermijdelijk: beoordelen of
 * een reactie weg moet, kan niet zonder te lezen wat er staat — en daarom zit dit scherm achter
 * `interaction.comment.moderate` en niet achter "berichten opstellen".
 *
 * **Wie meldde, staat er niet bij.** Niet verborgen achter een knop: de server geeft het niet. Een
 * melder die zichtbaar wordt, meldt de tweede keer niet meer, en dat is precies waarom de meldknop
 * van het oude platform in de praktijk niet werkte.
 *
 * Twee knoppen, en ze zijn beide een beslissing: weghalen, of laten staan. Er is geen derde optie
 * "later" — een wachtrij waarin alles blijft staan, is geen wachtrij.
 */
export function ModerationPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useAdminToast();

  const reports = useQuery({
    queryKey: ['moderation'],
    queryFn: async (): Promise<CommentReport[]> => {
      const { data, error } = await api.GET('/api/v1/moderation/comments', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as CommentReportList).items;
    },
  });
  const items = reports.data ?? [];

  return (
    <AdminPage narrow>
      <PageHead title={t('moderation.title')} sub={t('beheer.opvolging.moderation.sub')} />

      <Card>
        <CardHead
          title={t('beheer.opvolging.moderation.queue')}
          count={reports.data ? items.length : undefined}
          countAttention={items.length > 0}
        />
        {reports.isPending ? (
          <CardLoading rows={3} />
        ) : reports.isError ? (
          <LoadError onRetry={() => void reports.refetch()} error={reports.error} />
        ) : items.length === 0 ? (
          <Empty
            icon={<AdminIcon name="flag" size={26} />}
            title={t('beheer.opvolging.moderation.emptyTitle')}
            text={t('moderation.emptyHint')}
          />
        ) : (
          items.map((report) => (
            <ReportItem key={report.commentId} report={report} onDone={toast.show} />
          ))
        )}
        <DecisionHistory />
      </Card>
      {toast.element}
    </AdminPage>
  );
}

/**
 * Eén gemelde reactie: oranje tegel met vlag, hoeveel keer gemeld, onder welk bericht, de tekst en
 * van wie ze is. Van wie de meldingen kwamen, staat er nooit bij.
 */
function ReportItem({
  report,
  onDone,
}: {
  report: CommentReport;
  onDone: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();

  const act = useMutation({
    mutationFn: async (action: 'delete' | 'dismiss') => {
      const path = { commentId: report.commentId };
      const { error } =
        action === 'delete'
          ? await api.POST('/api/v1/moderation/comments/{commentId}/delete', { params: { path } })
          : await api.POST('/api/v1/moderation/comments/{commentId}/dismiss', { params: { path } });
      if (error) throw error;
    },
    onSuccess: (_result, action) => {
      void queryClient.invalidateQueries({ queryKey: ['moderation'] });
      onDone(action === 'delete' ? t('moderation.removed') : t('moderation.dismissed'));
    },
    onError: (error) =>
      toast.show({ tone: 'danger', title: problemDetail(error) ?? t('errors.generic') }),
  });

  return (
    <div className="a-item" data-testid="report-card">
      <Tile tone="orange">
        <AdminIcon name="flag" size={20} />
      </Tile>
      <div className="a-item-info">
        <div className="a-item-top">
          <Badge tone="attention">
            {t('beheer.opvolging.moderation.reports', { count: report.reportCount })}
          </Badge>
          <span className="a-meta">
            {t('beheer.opvolging.moderation.under', {
              post: report.postTitle,
              when: relativeMoment(report.commentCreatedAt),
            })}
          </span>
        </div>
        {report.deleted ? (
          <div className="a-muted">{t('moderation.alreadyRemoved')}</div>
        ) : (
          <div className="o-quote">{`"${report.body}"`}</div>
        )}
        {report.reasons.length > 0 ? (
          <div className="a-meta">
            {t('beheer.opvolging.moderation.reasons', { reasons: report.reasons.join(' · ') })}
          </div>
        ) : null}
        <div className="a-meta">
          {t('beheer.opvolging.moderation.by', { name: report.authorName })}
        </div>
      </div>
      <div className="a-actions">
        <Btn
          variant="sec"
          size="sm"
          testId="moderation-dismiss"
          disabled={act.isPending}
          aria-busy={act.isPending && act.variables === 'dismiss'}
          onClick={() => act.mutate('dismiss')}
        >
          {t('moderation.dismiss')}
        </Btn>
        {!report.deleted ? (
          <Btn
            variant="danger-sec"
            size="sm"
            icon={<AdminIcon name="eyeOff" />}
            testId="moderation-delete"
            disabled={act.isPending}
            aria-busy={act.isPending && act.variables === 'delete'}
            onClick={() => act.mutate('delete')}
          >
            {t('beheer.opvolging.moderation.remove')}
          </Btn>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Wat er eerder besloten is (Extra 6, punt 5), onderaan de kaart en ingeklapt: het is geen werk dat
 * wacht. Wie beoordeelt, heeft het wél nodig - om na te kijken wat er eerder met dezelfde persoon
 * gebeurde, en om bij een klacht achteraf te kunnen zeggen wie wat besliste.
 *
 * Meteen opgehaald en niet pas bij openklikken: de knop toont het aantal ("Eerdere beslissingen
 * (4)").
 */
function DecisionHistory() {
  const { t } = useTranslation();
  const { api } = useSession();
  const [open, setOpen] = useState(false);

  const decisions = useQuery({
    queryKey: ['moderation', 'decisions'],
    queryFn: async (): Promise<ModerationDecision[]> => {
      const { data, error } = await api.GET('/api/v1/moderation/decisions', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as ModerationDecisionList).items;
    },
  });
  const items = decisions.data ?? [];

  return (
    <div data-testid="moderation-history">
      <button
        type="button"
        className="a-item"
        style={{ width: '100%', textAlign: 'left' }}
        aria-expanded={open}
        data-testid="moderation-history-toggle"
        onClick={() => setOpen(!open)}
      >
        <div className="a-item-info">
          <div className="a-name a-muted">
            {decisions.data
              ? t('beheer.opvolging.moderation.history', { count: items.length })
              : t('beheer.opvolging.moderation.historyPlain')}
          </div>
        </div>
        <AdminIcon
          name="chevronDown"
          size={16}
          style={{ transform: open ? 'rotate(180deg)' : undefined, flexShrink: 0 }}
        />
      </button>

      {open ? (
        decisions.isPending ? (
          <CardLoading rows={3} />
        ) : decisions.isError ? (
          <LoadError onRetry={() => void decisions.refetch()} error={decisions.error} />
        ) : items.length === 0 ? (
          <Empty
            icon={<AdminIcon name="history" size={26} />}
            title={t('moderation.historyEmpty')}
          />
        ) : (
          items.map((decision) => (
            <div
              key={`${decision.commentId}-${decision.decidedAt}`}
              className="a-item"
              data-testid="moderation-decision"
            >
              <div className="a-item-info">
                <div className="a-item-top">
                  <Badge tone={decision.outcome === 'removed' ? 'danger' : 'neutral'}>
                    {t(`moderation.outcome.${decision.outcome}`)}
                  </Badge>
                  <span className="a-meta">
                    {formatDateTime(decision.decidedAt)}
                    {decision.decidedByName ? ` · ${decision.decidedByName}` : ''} ·{' '}
                    {t('beheer.opvolging.moderation.reports', { count: decision.reportCount })}
                  </span>
                </div>
                <div className="a-meta">
                  {t('beheer.opvolging.moderation.underPost', { post: decision.postTitle })}
                </div>
                <div className={decision.body ? 'o-quote' : 'a-muted'}>
                  {decision.body ? `"${decision.body}"` : t('moderation.bodyRemoved')}
                </div>
                <div className="a-meta">
                  {t('beheer.opvolging.moderation.by', { name: decision.authorName })}
                </div>
              </div>
            </div>
          ))
        )
      ) : null}
    </div>
  );
}
